import { AnyObj, removeUndefinedFields } from "../../utils"
import {
  DocumentData,
  DocumentReference,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  updateDoc,
} from "firebase/firestore"
import React, { useEffect, useState } from "react"

import { Button } from "@mui/material"
import { DisableMessage } from "../../utils/DisableMessage"
import { data } from "react-router-dom"
import { db } from "../../firebase"
import { PopupService } from "react-ui-components/components/PopupService"
import { VideoData } from "./VideoPlayer"
import VideoDashboard from "react-ui-components/components/Video/VideoDashboard"
import { VideoCategory } from "react-ui-components/components/Video/IVideoData"

const PAGE_SIZE = 100 // Increased to load more videos at once

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Unexpected error"

const VideoManagerIn: React.FC = () => {
  const [initialVideos, setInitialVideos] = useState<any[]>([])
  const [categories, setCategories] = useState<VideoCategory[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const categoriesRef = collection(db, "video-categories")
      const q = query(categoriesRef, orderBy("order"))
      const snapshot = await getDocs(q)

      if (!snapshot.empty) {
        const cats = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as VideoCategory))
        setCategories(cats)
      }
    } catch (error) {
      console.error("Error fetching categories:", error)
    }
  }

  // Fetch videos
  const fetchVideos = async () => {
    setIsLoading(true)

    try {
      const videosRef = collection(db, "videos")
      const q = query(videosRef, orderBy("order"), limit(PAGE_SIZE))
      const snapshot = await getDocs(q)

      if (!snapshot.empty) {
        const newVideos = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as VideoData))
        setInitialVideos(newVideos)
      }
    } catch (error) {
      console.error("Error fetching videos:", error)
    }

    setIsLoading(false)
  }

  // Handle Create/Update Video
  const handleSave = async (video: any, isEdit?: boolean, currentVideo?: any) => {
    try {
      const videoRef =
        isEdit && currentVideo ? doc(db, "videos", currentVideo.id) : doc(collection(db, "videos"))

      await setDoc(videoRef, removeUndefinedFields({ ...video, id: videoRef.id } ) as any, {
        merge: true,
      })
      
      // Refresh videos
      await fetchVideos()
    } catch (error) {
      console.error("Error saving video:", error)
      PopupService.setPopup(getErrorMessage(error))
    }
  }

  // Handle Delete Video
  const handleDelete = async (video: any) => {
    try {
      await deleteDoc(doc(db, "videos", video.id))
      setInitialVideos(initialVideos?.filter((x) => x.id !== video.id) || [])
    } catch (error) {
      console.error("Error deleting video:", error)
    }
  }

  // Handle Clone Video
  const handleClone = async (video: any) => {
    try {
      const newDocRef = doc(collection(db, "videos"))
      const formData = {
        ...video,
        id: newDocRef.id,
        title: `${video.title || newDocRef.id} (Copy)`,
      }

      await setDoc(newDocRef, formData)
      setInitialVideos((prev) => [...prev, { ...formData }])
    } catch (error) {
      console.error("Error cloning video:", error)
    }
  }

  // Handle Create/Update Category
  const handleSaveCategory = async (category: VideoCategory) => {
    try {
      const categoryRef = category.id && category.id.startsWith('cat_') && !categories.find(c => c.id === category.id)
        ? doc(collection(db, "video-categories"))
        : doc(db, "video-categories", category.id)

      await setDoc(categoryRef, removeUndefinedFields({ ...category, id: categoryRef.id }) as any, {
        merge: true,
      })
      
      // Refresh categories
      await fetchCategories()
    } catch (error) {
      console.error("Error saving category:", error)
      PopupService.setPopup(getErrorMessage(error))
    }
  }

  // Handle Delete Category
  const handleDeleteCategory = async (category: VideoCategory) => {
    try {
      await deleteDoc(doc(db, "video-categories", category.id))
      setCategories(categories.filter((x) => x.id !== category.id))
    } catch (error) {
      console.error("Error deleting category:", error)
      PopupService.setPopup(getErrorMessage(error))
    }
  }

  useEffect(() => {
    fetchCategories()
    fetchVideos()
  }, [])

  return (
    <div>
      <VideoDashboard
        onSaveVideo={handleSave}
        onDeleteVideo={handleDelete}
        onDuplicateVideo={handleClone}
        onSaveCategory={handleSaveCategory}
        onDeleteCategory={handleDeleteCategory}
        initialCategories={categories}
        initialVideos={initialVideos as any}
        DisabledMessage={({ disabled }: any) => <DisableMessage disabled={disabled} />}
      />
    </div>
  )
}

export default VideoManagerIn
